import React, { useEffect, useState, useRef } from 'react'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { Ellipsis, Plus, Trash, X } from 'lucide-react'
import axios from '../../../config/axios'
import { AddInStep } from '../../../pages/ClientPanel/Pipelines/style'

interface PropsInterface {
  closeModal: () => void
}

const DEFAULT_HEX_COLOR = '#99D3EE'

const normalizeHexColor = (value: string): string | null => {
  const sanitizedValue = String(value || '').trim().replace('#', '')
  if (/^[0-9a-fA-F]{3}$/.test(sanitizedValue)) {
    const [r, g, b] = sanitizedValue.split('')
    return `#${`${r}${r}${g}${g}${b}${b}`.toUpperCase()}`
  }
  if (/^[0-9a-fA-F]{6}$/.test(sanitizedValue)) {
    return `#${sanitizedValue.toUpperCase()}`
  }
  return null
}

const getHexColorOrDefault = (value?: string | null): string => {
  return normalizeHexColor(String(value || '')) || DEFAULT_HEX_COLOR
}

const OptionDiv: React.FC<{ children: React.ReactNode, onClick?: () => void, onMouseDown?: () => void }> = ({ children, onClick, onMouseDown }) => {
  return (
    <S.InputDiv style={{ flexDirection: 'row', alignItems: 'center', gap: '.5rem', cursor: 'pointer' }} onMouseDown={onMouseDown} onClick={onClick}> {children} </S.InputDiv>
  )
}

const ColorDiv: React.FC<{ color: string }> = ({ color }) => {
  return <div style={{ width: '22px', height: '22px', backgroundColor: color, borderRadius: '50%' }} />
}

const ViewOrEditTags: React.FC<PropsInterface> = ({ closeModal }) => {
  const [tags, setTags] = useState<any[]>([])
  const [dropDownIndex, setDropDownIndex] = useState<number | null>(null)
  const [dropdownCoords, setDropdownCoords] = useState<{ top: number, left: number, width: number } | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])
  const [newNameInput, setNewNameInput] = useState<string>('')
  const [newColorInput, setNewColorInput] = useState<string>(DEFAULT_HEX_COLOR)
  const isChangingColorRef = useRef(false)
  const isDeletingTagRef = useRef(false)

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/tag`)
        if (response.data) {
          const normalizedTags = (response.data.data || []).map((tag: any) => ({
            ...tag,
            color: getHexColorOrDefault(tag.color),
          }))
          setTags(normalizedTags)
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchTags()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isOutside = dropdownRef.current && !dropdownRef.current.contains(target)

      if (isOutside && dropDownIndex !== null) {
        handleSaveEditedTag(dropDownIndex)
        setDropDownIndex(null)
      }
    }

    if (dropDownIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropDownIndex, newNameInput, tags])

  const handleIsEditingTag = (index: number) => {
    const tag = tags[index]
    if (!tag) return

    if (tag.inUse) {
      alert('Tag em uso: somente cor pode ser alterada.')
    }

    const rowElement = rowRefs.current[index]
    if (rowElement) {
      const rect = rowElement.getBoundingClientRect()
      const width = 300
      const dropdownHeight = 290
      let top = rect.bottom + 6
      if (top + dropdownHeight > window.innerHeight - 20) {
        top = Math.max(16, rect.top - dropdownHeight - 6)
      }
      const left = Math.max(16, Math.min(rect.right - width, window.innerWidth - width - 16))
      setDropdownCoords({ top, left, width })
    }

    setDropDownIndex(index)
    setNewNameInput(tag.name)
    setNewColorInput(getHexColorOrDefault(tag.color))
  }

  const handleSaveEditedTag = (index: number | null = dropDownIndex) => {
    if (index === null) return

    const tag = tags[index]
    if (tag?.inUse) {
      setDropDownIndex(null)
      return
    }

    const nameToSave = String(newNameInput).trim()
    if (!nameToSave) {
      alert('Nome da tag é obrigatório.')
      return
    }

    setTags(prev => prev.map((tag, idx) => idx === index ? { ...tag, name: nameToSave, updated: true } : tag))
    setDropDownIndex(null)
  }

  const handleDeleteTag = async () => {
    if (dropDownIndex === null) return

    const tagToDelete = tags[dropDownIndex]
    if (tagToDelete?.inUse) {
      alert('Tag em uso não pode ser excluída.')
      return
    }

    if (tagToDelete?.id) {
      try {
        await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/tag/${tagToDelete.id}`)
      } catch (error) {
        console.error('Erro ao excluir tag:', error)
      }
    }

    setTags(prev => prev.filter((_, index) => index !== dropDownIndex))
    setDropDownIndex(null)
  }

  const handleChangeColor = (index: number, color: string) => {
    const normalizedColor = getHexColorOrDefault(color)
    isChangingColorRef.current = true
    setTags(prev => prev.map((tag, idx) => idx === index ? { ...tag, color: normalizedColor, updated: true } : tag))
  }

  const handleHexInputChange = (index: number, rawValue: string) => {
    isChangingColorRef.current = true
    setNewColorInput(rawValue)
    const normalizedColor = normalizeHexColor(rawValue)
    if (normalizedColor) {
      setTags(prev => prev.map((tag, idx) => idx === index ? { ...tag, color: normalizedColor, updated: true } : tag))
    }
  }

  const handleColorPickerChange = (index: number, color: string) => {
    const normalizedColor = getHexColorOrDefault(color)
    setNewColorInput(normalizedColor)
    handleChangeColor(index, normalizedColor)
  }

  const handleSaveTags = async () => {
    try {
      const payload = tags.map((tag) => ({
        id: tag.id,
        name: String(tag.name || '').trim(),
        color: getHexColorOrDefault(tag.color),
        updated: tag.updated || false,
      }))

      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/tag`, payload)
      if (response.data) {
        closeModal()
      }
    } catch (error: any) {
      console.error(error)
      const errorMessage = error?.response?.data?.message || 'Erro ao salvar tags. Tente novamente.'
      alert(errorMessage)
    }
  }

  return (
    <ModalTest>
      <S.ContainerOfModal width='600px'>
        <S.Header>
          <S.HeaderTitle>Tags</S.HeaderTitle>
          <S.CloseDiv onClick={closeModal}> <X size={20} /></S.CloseDiv>
        </S.Header>
        <S.FormContainer as="div">
          {tags.map((tag, index) => (
            <S.InputDiv
              key={tag.id || `new-${index}`}
              ref={(el) => (rowRefs.current[index] = el)}
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <ColorDiv color={getHexColorOrDefault(tag.color)} />
                <S.Label style={{ fontWeight: 400 }}>{tag.name}</S.Label>
              </div>
              <Ellipsis size={20} style={{ cursor: 'pointer' }} color='#696969' onClick={() => handleIsEditingTag(index)} />
              {dropDownIndex === index && dropdownCoords && (
                <div
                  ref={dropdownRef}
                  style={{
                    position: 'fixed',
                    top: dropdownCoords.top,
                    left: dropdownCoords.left,
                    width: dropdownCoords.width,
                    backgroundColor: 'white',
                    padding: '.8rem',
                    borderRadius: '5px',
                    zIndex: 9999,
                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <S.FormContainer as="div">
                    <S.Input
                      placeholder='Digite aqui'
                      value={newNameInput}
                      onChange={(e) => setNewNameInput(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleSaveEditedTag()
                          setDropDownIndex(null)
                        }
                      }}
                      onBlur={(e) => {
                        if (isChangingColorRef.current || isDeletingTagRef.current) {
                          isChangingColorRef.current = false
                          isDeletingTagRef.current = false
                          return
                        }
                        handleSaveEditedTag()
                        setDropDownIndex(null)
                      }}
                    />
                    <OptionDiv
                      onMouseDown={() => { isDeletingTagRef.current = true }}
                      onClick={() => { handleDeleteTag() }}
                    >
                      <Trash size={20} style={{ cursor: 'pointer' }} color='#BF383B' /> <span style={{ fontSize: '15px', fontWeight: 400, color: '#BF383B' }}>Excluir</span>
                    </OptionDiv>
                    <S.InputDiv style={{ gap: '.5rem' }}>
                      <S.Label style={{ fontWeight: 500 }}>Cor (HEX)</S.Label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
                        <ColorDiv color={getHexColorOrDefault(tags[index]?.color)} />
                        <input
                          type='color'
                          value={getHexColorOrDefault(tags[index]?.color)}
                          onMouseDown={() => { isChangingColorRef.current = true }}
                          onChange={(event) => handleColorPickerChange(index, event.target.value)}
                          style={{ width: '40px', height: '32px', border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' }}
                        />
                        <S.Input
                          placeholder='#AABBCC'
                          value={newColorInput}
                          onMouseDown={() => { isChangingColorRef.current = true }}
                          onChange={(event) => handleHexInputChange(index, event.target.value)}
                        />
                      </div>
                    </S.InputDiv>
                  </S.FormContainer>
                </div>
              )}
            </S.InputDiv>
          ))}
          <AddInStep type="button" onClick={() => setTags([...tags, { name: 'Tag', color: DEFAULT_HEX_COLOR }])}><Plus />Criar nova tag</AddInStep>
        </S.FormContainer>
        <S.BottomDiv>
          <S.ButtonContainer>
            <S.Button type="button" onClick={closeModal} className='cancel'>Cancelar</S.Button>
            <S.Button type="button" onClick={handleSaveTags}>Salvar e fechar</S.Button>
          </S.ButtonContainer>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

export default ViewOrEditTags